import React from "react";
import { Checkbox, Switch } from "@mui/material";
import { useController, useFormContext, useWatch } from "react-hook-form";

import {
  StyledFormRow,
  StyledMatrixBodyRow,
  StyledMatrixCell,
  StyledMatrixCol,
  StyledMatrixHeader,
  StyledMatrixHeaderCell,
  StyledMatrixGroupLabel,
  StyledRowHelper,
  StyledRowInput,
  StyledRowLabel,
  StyledRowMeta,
  StyledSectionLabel,
  StyledCheckbox,
} from "./styles";

export const SectionLabel = ({ children }) => (
  <StyledSectionLabel>{children}</StyledSectionLabel>
);

export const FormRow = ({ label, helper, column = false, children }) => (
  <StyledFormRow column={column}>
    <StyledRowMeta>
      <StyledRowLabel>{label}</StyledRowLabel>
      {helper && <StyledRowHelper>{helper}</StyledRowHelper>}
    </StyledRowMeta>
    <StyledRowInput column={column}>{children}</StyledRowInput>
  </StyledFormRow>
);

export const MatrixGroupLabel = ({ children }) => (
  <StyledMatrixGroupLabel>{children}</StyledMatrixGroupLabel>
);

export const MatrixHeader = ({ channels }) => (
  <StyledMatrixHeader>
    <StyledMatrixCol />
    <StyledMatrixHeaderCell>On</StyledMatrixHeaderCell>
    {channels.map((c) => (
      <StyledMatrixHeaderCell key={c.key}>{c.label}</StyledMatrixHeaderCell>
    ))}
  </StyledMatrixHeader>
);

const MatrixCellSwitch = ({ name, disabled, onToggle }) => {
  const {
    field: { value, onChange },
  } = useController({ name, defaultValue: false });

  return (
    <StyledCheckbox
      size="small"
      checked={value || disabled}
      disabled={disabled}
      onChange={(e) => {
        const next = e.target.checked;
        onChange(next);
        onToggle?.(next);
      }}
      greyed={disabled}
    />
  );
};

const MatrixMasterSwitch = ({ channels, fieldName, onChannelToggle }) => {
  const { setValue } = useFormContext();
  const names = channels.map((c) => fieldName(c.key));
  const values = useWatch({ name: names }) || [];
  const master = values.some(Boolean);

  const handleChange = (e) => {
    const next = e.target.checked;
    channels.forEach((c, i) => {
      setValue(names[i], next, { shouldDirty: true });
    });
    const keys = channels.map((c) => c.key);
    onChannelToggle?.(keys, next);
  };

  return (
    <Switch
      size="small"
      checked={master}
      onChange={handleChange}
      sx={{ padding: 0, margin: 0, transform: "scale(0.8)" }}
    />
  );
};

export const MatrixRow = ({
  label,
  helper,
  channels,
  fieldName,
  onCellToggle,
}) => {
  const names = channels.map((c) => fieldName(c.key));
  const values = useWatch({ name: names }) || [];

  const googleChatEnabled = useWatch({ name: "googleChatEnabled" });
  const emailEnabled = useWatch({ name: "emailEnabled" });
  const inAppEnabled = useWatch({ name: "inAppEnabled" });

  const getDisabled = (key) => {
    switch (key) {
      case "google_chat":
        return !googleChatEnabled;
        break;
      case "email":
        return !emailEnabled;
        break;
      case "in_app":
        return !inAppEnabled;
        break;
      default:
        break;
    }
  };

  return (
    <StyledMatrixBodyRow>
      <StyledRowMeta>
        <StyledRowLabel>{label}</StyledRowLabel>
        {helper && <StyledRowHelper>{helper}</StyledRowHelper>}
      </StyledRowMeta>
      <StyledMatrixCell />
      {channels.map((c) => (
        <StyledMatrixCell key={c.key}>
          <MatrixCellSwitch
            name={fieldName(c.key)}
            disabled={getDisabled(c.key)}
            onToggle={(next) => onCellToggle?.(c.key, next)}
          />
        </StyledMatrixCell>
      ))}
    </StyledMatrixBodyRow>
  );
};
